import axios from "axios";
import { getIdToken } from "./Auth";

const inFlightPostRequests = new Map();

const getPostRequestKey = (endpoint, data, Authorizer, options) =>
  JSON.stringify({
    endpoint,
    data,
    authorizer: Boolean(Authorizer),
    headers: options.headers ?? {},
    includeResponse: Boolean(options.includeResponse),
  });

const createApiError = (message, status, data, meta = {}) => {
  const error = new Error(message);
  error.status = status;
  error.response = {
    status,
    data,
  };
  error.code = meta.code;
  error.endpoint = meta.endpoint;
  return error;
};

const createHeaders = async (Authorizer) => {
  const headers = {
    "Content-Type": "application/json",
  };

  if (!Authorizer) {
    return headers;
  }

  const idToken = await getIdToken();
  if (!idToken) {
    throw createApiError("Unauthorized", 401, { message: "Unauthorized" });
  }
  // console.log("Authorization: "+ idToken);
  return {
    ...headers,
    Authorization: `Bearer ${idToken}`,
  };
};

const toSafeApiError = (error, endpoint) => {
  const status = error.status ?? error.response?.status;

  if (status != null) {
    return createApiError(
      error.message || "API request failed",
      status,
      error.response?.data,
      {
        code: error.code,
        endpoint,
      }
    );
  }

  if (error.request && !error.response) {
    return createApiError("Network Error", 0, { message: "Network Error" }, {
      code: error.code,
      endpoint,
    });
  }

  return createApiError(error.message || "API request failed", undefined, undefined, {
    code: error.code,
    endpoint,
  });
};

export const get = async (endpoint, params = {}, Authorizer) => {
  try {
    const api_headers = await createHeaders(Authorizer);
    const response = await axios.get(endpoint, {
      headers: api_headers,
      params,
    });
    return response.data;
  } catch (error) {
    const safeError = toSafeApiError(error, endpoint);
    console.error("GET request failed:", {
      message: safeError.message,
      status: safeError.status,
      code: safeError.code,
      endpoint: safeError.endpoint,
    });
    throw safeError;
  }
};

export const post = async (endpoint, data, Authorizer, options = {}) => {
  const requestKey = getPostRequestKey(endpoint, data, Authorizer, options);
  const existingRequest = inFlightPostRequests.get(requestKey);
  if (existingRequest) {
    return existingRequest;
  }

  let request;
  request = (async () => {
    try {
      const apiHeaders = await createHeaders(Authorizer);
      const headers = {
        ...(options.headers ?? {}),
        ...apiHeaders,
      };
      const response = await axios.post(endpoint, data, {
        headers,
      });
      return options.includeResponse
        ? { status: response.status, data: response.data }
        : response.data;
    } catch (error) {
      const safeError = toSafeApiError(error, endpoint);
      console.error("POST request failed:", {
        message: safeError.message,
        status: safeError.status,
        code: safeError.code,
        endpoint: safeError.endpoint,
      });
      throw safeError;
    } finally {
      if (inFlightPostRequests.get(requestKey) === request) {
        inFlightPostRequests.delete(requestKey);
      }
    }
  })();

  inFlightPostRequests.set(requestKey, request);
  return request;
};
